import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import travelGuideObject from '@/entities/travelGuide/interface'

import {
  ALL_TRAVEL_GUIDES_BLOCKS_QUERY,
  SINGLE_TRAVEL_GUIDES_BLOCK_QUERY,
  CREATE_TRAVEL_GUIDES_BLOCK_MUTATION,
  UPDATE_TRAVEL_GUIDES_BLOCK_MUTATION,
  DELETE_TRAVEL_GUIDES_BLOCK_MUTATION,
  FORCE_DELETE_TRAVEL_GUIDES_BLOCK_MUTATION,
  ADD_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION,
  REMOVE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION,
  CREATE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION,
  DELETE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION,
  UPDATE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsTravelGuidesBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TRAVEL_GUIDES_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.travelGuidesBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TRAVEL_GUIDES_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.travelGuidesBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TRAVEL_GUIDES_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.travelGuidesBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TRAVEL_GUIDES_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.travelGuidesBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TRAVEL_GUIDES_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travelGuidesBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TRAVEL_GUIDES_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travelGuidesBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TRAVEL_GUIDES_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsTravelGuidesBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TRAVEL_GUIDES_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsTravelGuidesBlock
    })
  },

  assignTravelGuides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION, variables)
  },

  unassignTravelGuides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION, variables)
  },

  createTravelGuides: async (id: string, items: Array<travelGuideObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuides: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION, variables)
  },

  deleteTravelGuides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_MUTATION, variables)
  },

  updateTravelGuidesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_GUIDES_BLOCK_TRAVEL_GUIDES_ORDER_MUTATION, variables)
  },
}

export default cmsTravelGuidesBlockRepository
